import { AnalyticsEvent, analyticsService } from '~/services/analytics';
import {
  DOMAIN,
  REASON,
  reportFailure,
  reportWarning,
} from '~/services/telemetry';
import type { UserStore } from '~/stores/UserStore';

import type { MembershipCardChangeEvent } from './domain/cardChange';
import { membershipCardReturnUrl } from './domain/clubEntry';
import type {
  MembershipManageEvent,
  MembershipManageIncident,
} from './domain/manage';
import type { MembershipFormIncident } from './domain/purchase';
import {
  createCardFormFactory,
  createGateway,
  createMarkers,
  reportFormIncident,
} from './infrastructure';
import {
  MembershipCardChangeStore,
  type MembershipCardUpdate,
} from './store/MembershipCardChangeStore';
import { MembershipManageStore } from './store/MembershipManageStore';
import type { MembershipStore } from './store/MembershipStore';

const trackManage = (
  event: MembershipManageEvent | MembershipCardChangeEvent,
): void => {
  switch (event.name) {
    case 'cancelled':
      analyticsService.track(
        AnalyticsEvent.MEMBERSHIP_CANCELLED,
        event.payload,
      );
      return;
    case 'planChanged':
      analyticsService.track(
        AnalyticsEvent.MEMBERSHIP_PLAN_CHANGED,
        event.payload,
      );
      return;
    case 'planChangeKept':
      analyticsService.track(
        AnalyticsEvent.MEMBERSHIP_PLAN_CHANGE_KEPT,
        event.payload,
      );
      return;
    case 'cardUpdated':
      analyticsService.track(
        AnalyticsEvent.MEMBERSHIP_CARD_UPDATED,
        event.payload,
      );
      return;
    case 'cardUpdateFailed':
      analyticsService.track(
        AnalyticsEvent.MEMBERSHIP_CARD_UPDATE_FAILED,
        event.payload,
      );
      return;
    default: {
      const unhandled: never = event;
      return unhandled;
    }
  }
};

/** Sentry reports of the member card and the "Add card" sheet (`?modal=club-card`). Extras carry the classification only — never a payment field. */
const reportManage = (
  incident: MembershipManageIncident | MembershipFormIncident,
): void => {
  switch (incident.reason) {
    case 'manageFailed':
      reportFailure(DOMAIN.membership, 'Membership request failed', {
        reason: REASON.membership.manageFailed,
        fingerprint: [incident.operation, incident.kind],
        extras: {
          operation: incident.operation,
          kind: incident.kind,
          status: incident.status,
          code: incident.code,
          upstreamOperation: incident.upstream?.operation,
          upstreamStatus: incident.upstream?.upstreamStatus,
        },
      });
      return;
    case 'cardRefused':
      reportWarning(DOMAIN.membership, 'Card refused', {
        reason: REASON.membership.cardRefused,
        fingerprint: [incident.code ?? String(incident.status ?? 'unknown')],
        extras: {
          flow: incident.flow,
          status: incident.status,
          code: incident.code,
        },
      });
      return;
    case 'cardUpdateFailed':
      reportFailure(DOMAIN.membership, 'Card update failed', {
        reason: REASON.membership.cardUpdateFailed,
        fingerprint: [incident.stage, incident.kind],
        extras: {
          stage: incident.stage,
          kind: incident.kind,
          status: incident.status,
          code: incident.code,
          upstreamOperation: incident.upstream?.operation,
          upstreamStatus: incident.upstream?.upstreamStatus,
        },
      });
      return;
    case 'adyenInitFailed':
    case 'cardFormChunkFailed':
      reportFormIncident(incident);
      return;
    default: {
      const unhandled: never = incident;
      return unhandled;
    }
  }
};

/**
 * Composition root of the member card: cancel, plan switch and "Keep current
 * plan" over the never-retried gateway; answers go into the membership store
 * in place.
 */
export const createMembershipManageStore = (
  membershipStore: MembershipStore,
): MembershipManageStore =>
  new MembershipManageStore({
    gateway: createGateway(),
    applyMembership: membershipStore.applyMembership,
    showNotice: membershipStore.showNotice,
    reloadMembership: () => {
      void membershipStore.loadMe();
    },
    reloadPlans: () => {
      void membershipStore.loadPlans();
    },
    track: trackManage,
    report: reportManage,
    isBlocked: () => membershipStore.restoration === 'polling',
  });

/**
 * Shows a saved card's banner with the new card; a `/me` answer is shown in
 * place, otherwise `/me` and the payment history are re-read, since a card
 * change answer has no scheduled plan change and no payment issue, and a
 * restored membership has a new charge.
 */
const showCardUpdated = (
  membershipStore: MembershipStore,
  { card, me }: MembershipCardUpdate,
  restored: boolean,
): void => {
  const notice = { kind: 'cardUpdated', restored, card } as const;
  if (me) {
    membershipStore.applyMembership(me, notice);
    return;
  }
  membershipStore.showNotice(notice);
  void membershipStore.reloadAfterCardChange();
};

/**
 * Composition root of the "Add card" sheet (`?modal=club-card`): the card change store over the
 * never-retried gateway, the same lazy Adyen card form as Join, the
 * session markers, and the membership store it updates once the card is saved.
 */
export const createMembershipCardChangeStore = (
  membershipStore: MembershipStore,
  userStore: UserStore,
): MembershipCardChangeStore =>
  new MembershipCardChangeStore({
    gateway: createGateway(),
    createCardForm: createCardFormFactory(userStore),
    markers: createMarkers(userStore),
    returnUrl: () => membershipCardReturnUrl(window.location.origin),
    zoneId: () => membershipStore.zoneId,
    onCardUpdated: (update, restored) =>
      showCardUpdated(membershipStore, update, restored),
    onRestorePending: (me) => {
      if (me) {
        membershipStore.applyMembership(me);
      }
      membershipStore.awaitRestoration();
    },
    onMembershipChanged: () => {
      void membershipStore.loadMe();
    },
    reloadPage: () => {
      window.location.reload();
    },
    track: trackManage,
    report: reportManage,
  });
